/** Pure existing detection shared by Edge generation and browser prep. No logging or provider I/O. */
// Layer 1: Suspicious patterns that may indicate injection attempts
const SUSPICIOUS_PATTERNS = [
  /ignore\s+(all\s+)?(previous|prior|above)\s+(instructions?|prompts?|rules?)/i,
  /disregard\s+(all\s+)?(previous|prior|above)/i,
  /you\s+are\s+now\s+a/i,
  /new\s+instructions?:/i,
  /system\s+prompt/i,
  /reveal\s+(your|the)\s+(instructions?|prompt|rules?)/i,
  /output\s+(your|the)\s+(system|initial)\s+prompt/i,
  /\bDAN\b/,  // "Do Anything Now" jailbreak
  /jailbreak/i,
  /bypass\s+(safety|restrictions?|rules?)/i,
  /pretend\s+(to\s+be|you\s+are)/i,
  /act\s+as\s+if\s+you/i,
  /forget\s+(everything|all|your)/i,
  /override\s+(your|the|all)/i,
  /admin\s+mode/i,
  /developer\s+mode/i,
  /sudo/i,
  /\[system\]/i,
  /\[INST\]/i,
  /<\|im_start\|>/i,
  /<\|im_end\|>/i,
  /<<SYS>>/i,
  /<\/s>/i,
];

// Additional context-aware patterns for coaching domain
const COACHING_DOMAIN_PATTERNS = [
  /reveal\s+(all\s+)?(leader|coach|client)\s+data/i,
  /show\s+me\s+(all\s+)?(the\s+)?other\s+(leaders?|coaches?|users?)/i,
  /access\s+(other|all)\s+(users?|accounts?)/i,
  /pretend\s+i\s+am\s+(a\s+)?(coach|admin|leader)/i,
];

export type RiskLevel = 'low' | 'medium' | 'high';

export interface InjectionDetectionResult {
  isSuspicious: boolean;
  matchedPatterns: string[];
  riskLevel: RiskLevel;
}

/**
 * Layer 1: Detect potential prompt injection attempts
 */
export function detectInjectionAttempt(input: string): InjectionDetectionResult {
  const matched: string[] = [];

  // Check general patterns
  for (const pattern of SUSPICIOUS_PATTERNS) {
    if (pattern.test(input)) {
      matched.push(pattern.source);
    }
  }

  // Check coaching domain patterns
  for (const pattern of COACHING_DOMAIN_PATTERNS) {
    if (pattern.test(input)) {
      matched.push(pattern.source);
    }
  }

  // Determine risk level
  let riskLevel: RiskLevel = 'low';
  if (matched.length >= 3) {
    riskLevel = 'high';
  } else if (matched.length >= 1) {
    riskLevel = 'medium';
  }

  return {
    isSuspicious: matched.length > 0,
    matchedPatterns: matched,
    riskLevel,
  };
}

function suspicious(value: string): boolean {
  // Detection views only. Exact provenance always uses the original source bytes.
  const views = [value, value.normalize("NFKC").replace(/[\u200b-\u200f\u202a-\u202e\u2060-\u2069\ufeff]/gu, "")];
  return views.some(view => detectInjectionAttempt(view).isSuspicious
    || /<\|(?:system|developer|assistant|user|endoftext)[^>]*\|>|\[\/?(?:system|developer|assistant)\]/i.test(view));
}
export function containsSuspiciousNeutralText(value: unknown): boolean {
  if (typeof value === "string") return suspicious(value);
  if (Array.isArray(value)) return value.some(containsSuspiciousNeutralText);
  if (value !== null && typeof value === "object") return Object.values(value).some(containsSuspiciousNeutralText);
  return false;
}
